import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { lumi } from "../lib/lumi";
import {Share2} from 'lucide-react';

// 分類定義
const CATEGORIES = {
  news: { zh: "最新消息", en: "News" },
  guide: { zh: "沖煮指南", en: "Brewing Guide" },
  beanstory: { zh: "豆單故事", en: "Bean Story" },
  events: { zh: "活動紀錄", en: "Events" }
};

interface Article {
  _id: string;
  title: string;
  titleEn?: string;
  slug: string;
  excerpt?: string;
  content: string;
  category: "news" | "guide" | "beanstory" | "events";
  tags?: string[];
  featuredImage?: string;
  author?: string;
  publishedAt?: string;
  status: "draft" | "published" | "archived";
  isFeatured?: boolean;
  viewCount?: number;
  seoTitle?: string;
  seoDescription?: string;
  createdAt: string;
}

const LabPage: React.FC = () => {
  const { category } = useParams<{ category?: string }>();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>(category || "all");
  const [showShareMenu, setShowShareMenu] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // 從 URL 判斷語系
  const currentLang = window.location.pathname.startsWith("/en") ? "en" : "zh";

  useEffect(() => {
    let cancelled = false;

    const cacheKey = `lab_articles:${currentLang}:${category || "all"}`;

    // 先用快取（避免每次切換分類都要等 DB）；有快取就直接渲染，不要再把 loading 打回 true 造成「一直轉」
    let hasHydratedFromCache = false;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached) as { list?: Article[] };
        if (Array.isArray(parsed?.list) && parsed.list.length > 0) {
          hasHydratedFromCache = true;
          setArticles(parsed.list);
          setLoading(false);
        }
      }
    } catch {
      // ignore cache parse errors
    }

    const fetchArticles = async () => {
      try {
        // 沒快取時才顯示全頁 loading；有快取就背景刷新
        if (!hasHydratedFromCache) setLoading(true);

        const filter: Record<string, any> = { status: "published" };
        if (category && category !== "all") {
          filter.category = category;
        }

        // 加上 timeout，避免請求 pending 導致 UI 永久轉圈
        const result = await Promise.race([
          lumi.entities.articles.list({
            filter,
            sort: { publishedAt: -1, createdAt: -1 },
            limit: 60,
            skip: 0
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Request timeout")), 8000))
        ]);

        const articlesList = Array.isArray(result) ? result : ((result as any)?.list || []);
        if (cancelled) return;

        if (Array.isArray(articlesList)) {
          setArticles(articlesList);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ list: articlesList }));
          } catch {
            // ignore storage quota errors
          }
        }
      } catch {
        // 避免噴錯干擾使用者；若已有快取就維持快取畫面
      } finally {
        // 無論成功/失敗都要保證結束 loading，避免永久轉圈
        if (!cancelled) setLoading(false);
      }
    };

    fetchArticles();

    return () => {
      cancelled = true;
    };
  }, [category, currentLang]);

  useEffect(() => {
    setSelectedCategory(category || "all");
  }, [category]);

  // 點擊外部關閉分享選單
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showShareMenu && !(e.target as HTMLElement).closest(".relative")) {
        setShowShareMenu(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showShareMenu]);

  const handleCategoryChange = (newCategory: string) => {
    if (newCategory === "all") {
      navigate(`/${currentLang}/lab`);
    } else {
      navigate(`/${currentLang}/lab/${newCategory}`);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLang === "en"
      ? date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
      : date.toLocaleDateString("zh-TW", { year: "numeric", month: "long", day: "numeric" });
  };

  const handleShare = (platform: string, article: Article) => {
    const url = `https://www.ongucoffee.com/${currentLang}/lab/${article.category}/${article.slug}`;
    const title = currentLang === "en" && article.titleEn ? article.titleEn : article.title;

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, "_blank");
        break;
      case "linkedin":
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
        break;
      case "copy":
        navigator.clipboard.writeText(url);
        setCopySuccess(article._id);
        setTimeout(() => setCopySuccess(null), 2000);
        break;
    }
    setShowShareMenu(null);
  };

  // SEO Meta 標籤
  const getSeoData = () => {
    const seoMap: Record<string, { zh: { title: string; desc: string }; en: { title: string; desc: string } }> = {
      news: {
        zh: { title: "最新消息 News｜ONGU Studio", desc: "關注 ONGU Studio 的最新動態，包括新豆上架、季節限定配方發布、門市活動與品牌公告。" },
        en: { title: "News - ONGU Studio", desc: "Latest updates, new bean releases, and brand announcements from ONGU Studio." }
      },
      beanstory: {
        zh: { title: "豆單故事 Bean Story｜產地與烘焙 - ONGU Studio", desc: "每一支咖啡豆都有屬於它的故事。從產地風土、處理法細節到烘焙曲線的詮釋，深入了解 ONGU Studio 豆單背後的風味旅程。" },
        en: { title: "Bean Story - ONGU Studio", desc: "Discover the stories behind our beans, from terroir and processing to the roasting profile." }
      },
      events: {
        zh: { title: "活動紀錄 Events｜課程與市集 - ONGU Studio", desc: "回顧 ONGU Studio 的各類活動紀錄，包含手沖課程、杯測分享會、品牌聯名與市集出攤點滴。" },
        en: { title: "Events - ONGU Studio", desc: "Recap of ONGU Studio events, including brewing workshops, cupping sessions, and market pop-ups." }
      },
      guide: {
        zh: { title: "沖煮指南 Brewing Guide｜教學 - ONGU Studio", desc: "探索各種咖啡沖煮技巧，從手沖數據、研磨度建議到萃取參數，讓你在家也能煮出一杯好咖啡。" },
        en: { title: "Brewing Guide - ONGU Studio", desc: "Explore brewing tips, grind size recommendations, and extraction parameters for the perfect cup at home." }
      }
    };

    if (category && seoMap[category]) {
      return seoMap[category][currentLang === "en" ? "en" : "zh"];
    }

    return {
      title: currentLang === "en" ? "Lab - ONGU Studio" : "醞玖製研中心 - ONGU Studio",
      desc: currentLang === "en"
        ? "Explore coffee stories, brewing guides, and latest news from ONGU Studio Lab."
        : "探索咖啡故事、沖煮指南與 ONGU Studio 醞玖製研中心的最新消息。"
    };
  };

  const { title: pageTitle, desc: pageDescription } = getSeoData();

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={"https://www.ongucoffee.com/" + currentLang + "/lab" + (category ? "/" + category : "")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={"https://www.ongucoffee.com/" + currentLang + "/lab" + (category ? "/" + category : "")} />
        <link rel="alternate" hreflang="zh" href={"https://www.ongucoffee.com/zh/lab" + (category ? "/" + category : "")} />
        <link rel="alternate" hreflang="en" href={"https://www.ongucoffee.com/en/lab" + (category ? "/" + category : "")} />
        <link rel="alternate" hreflang="x-default" href={"https://www.ongucoffee.com/zh/lab" + (category ? "/" + category : "")} />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100">
        {/* Toolbar：文章數量 + 分類下拉選單（取代原本一排 Pills） */}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] pt-8">
          <div className="flex flex-col gap-3 border-b border-black/10 dark:border-white/15 py-4 mb-10 md:mb-14 sm:flex-row sm:items-center sm:justify-between">
            <span
              className="text-xs text-black/60 dark:text-white/60"
              style={{ letterSpacing: '0.05em' }}
            >
              {currentLang === 'en'
                ? `${articles.length} articles`
                : `${articles.length} 篇文章`}
            </span>

            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="hidden sm:inline text-xs text-black/50 dark:text-white/50"
                style={{ letterSpacing: '0.05em' }}
              >
                {currentLang === 'en' ? 'Category' : '分類'}
              </span>
              <select
                value={category || 'all'}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="border border-black/15 dark:border-white/20 bg-white dark:bg-black px-3 py-1.5 text-xs text-black dark:text-white focus:border-black dark:focus:border-white focus:outline-none"
                style={{ letterSpacing: '0.04em' }}
              >
                <option value="all">
                  {currentLang === 'en' ? 'All' : '全部文章'}
                </option>
                {Object.entries(CATEGORIES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {currentLang === 'en' ? label.en : label.zh}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 文章列表 */}
        <section>
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] pb-16">
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
                <p className="mt-4 text-sm font-light tracking-wide text-gray-500 dark:text-gray-400">
                  {currentLang === "en" ? "Loading..." : "載入中..."}
                </p>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-lg font-light tracking-wide text-gray-500 dark:text-gray-400">
                  {currentLang === "en" ? "No articles found." : "目前沒有文章。"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <Link
                    key={article._id}
                    to={`/${currentLang}/lab/${article.category}/${article.slug}`}
                    className="group block bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    {/* 特色圖片 */}
                    {article.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9 bg-gray-200 dark:bg-gray-800 overflow-hidden">
                        <img
                          src={article.featuredImage}
                          alt={currentLang === "en" && article.titleEn ? article.titleEn : article.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}

                    {/* 文章資訊 */}
                    <div className="p-6">
                      {/* 分類標籤和分享按鈕 */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-light tracking-wider uppercase text-gray-500 dark:text-gray-400">
                            {currentLang === "en" ? CATEGORIES[article.category].en : CATEGORIES[article.category].zh}
                          </span>
                          {article.isFeatured && (
                            <span className="text-xs font-light tracking-wider uppercase text-yellow-600 dark:text-yellow-500">
                              ★ {currentLang === "en" ? "Featured" : "精選"}
                            </span>
                          )}
                        </div>

                        {/* 分享按鈕 */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setShowShareMenu(showShareMenu === article._id ? null : article._id);
                            }}
                            className="p-1.5 border border-gray-300 dark:border-gray-700 hover:border-gray-900 dark:hover:border-gray-100 transition-all duration-300"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>

                          {/* 分享選單 */}
                          {showShareMenu === article._id && (
                            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 shadow-xl z-50">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleShare("facebook", article);
                                }}
                                className="w-full px-4 py-2 text-left text-sm font-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              >
                                Facebook
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleShare("twitter", article);
                                }}
                                className="w-full px-4 py-2 text-left text-sm font-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-t border-gray-200 dark:border-gray-800"
                              >
                                Twitter
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleShare("linkedin", article);
                                }}
                                className="w-full px-4 py-2 text-left text-sm font-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-t border-gray-200 dark:border-gray-800"
                              >
                                LinkedIn
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleShare("copy", article);
                                }}
                                className="w-full px-4 py-2 text-left text-sm font-light hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-t border-gray-200 dark:border-gray-800"
                              >
                                {copySuccess === article._id ? (currentLang === "en" ? "Copied!" : "已複製！") : (currentLang === "en" ? "Copy Link" : "複製連結")}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 標題 */}
                      <h3 className="text-lg font-light tracking-wide text-gray-900 dark:text-gray-100 mb-3 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors line-clamp-2">
                        {currentLang === "en" && article.titleEn ? article.titleEn : article.title}
                      </h3>

                      {/* 摘要 */}
                      {article.excerpt && (
                        <p className="text-sm font-light text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                          {article.excerpt}
                        </p>
                      )}

                      {/* 日期和作者 */}
                      <div className="flex items-center justify-between text-xs font-light text-gray-500 dark:text-gray-500">
                        <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                        {article.author && <span>{article.author}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default LabPage;
